import React from 'react';

import {
	buildTestData,
} from 'Vulture/TestData';

export const formatTimeForJSON = (date) => {
	let selectedDate = new Date(date);
	// Convert to UTC time format HH:MM:SS
	const hours = selectedDate.getUTCHours().toString().padStart(2, '0');
	const minutes = selectedDate.getUTCMinutes().toString().padStart(2, '0');
	return `${hours}:${minutes}:00`;
};

export const formatTimeForPicker = (time) => {
	if (time) {
		// Time is stored as UTC HH:MM:SS, convert to local time for display
		let hoursMinutesSeconds = time.split(':');
		let stubDate = new Date();
		stubDate.setUTCHours(hoursMinutesSeconds[0]);
		stubDate.setUTCMinutes(hoursMinutesSeconds[1]);
		stubDate.setUTCSeconds('00');
		return stubDate;
	}

	return null;
};

/**
 * Normalizes time-only dates for comparison by setting year/month/day/seconds/milliseconds to match a reference date.
 * This allows proper comparison of just the time portion.
 */
export const normalizeTimeForComparison = (timeDate, referenceDate) => {
	timeDate.setFullYear(referenceDate.getFullYear());
	timeDate.setMonth(referenceDate.getMonth());
	timeDate.setDate(referenceDate.getDate());
	timeDate.setSeconds(referenceDate.getSeconds());
	timeDate.setMilliseconds(referenceDate.getMilliseconds());
};

/**
 * Handles onChange for start time pickers with showTimeSelectOnly.
 * Ensures start time is before end time, adjusting if necessary.
 */
export const handleStartTimeChange = (selectedTime, endTimeString) => {
	if (!selectedTime) {
		return null;
	}

	if (!endTimeString) {
		return formatTimeForJSON(selectedTime);
	}

	const endTime = formatTimeForPicker(endTimeString);

	// Normalize dates for time-only comparison
	normalizeTimeForComparison(selectedTime, endTime);

	if (selectedTime >= endTime) {
		// If start is not before end, set it to end - 15 minutes
		const newStartTime = new Date(endTime.getTime() - 15 * 60000);
		return formatTimeForJSON(newStartTime);
	}

	return formatTimeForJSON(selectedTime);
};

/**
 * Handles onChange for end time pickers with showTimeSelectOnly.
 * Ensures end time is after start time, adjusting if necessary.
 */
export const handleEndTimeChange = (selectedTime, startTimeString) => {
	if (!selectedTime) {
		return null;
	}

	if (!startTimeString) {
		return formatTimeForJSON(selectedTime);
	}

	const startTime = formatTimeForPicker(startTimeString);

	// Normalize dates for time-only comparison
	normalizeTimeForComparison(selectedTime, startTime);

	if (selectedTime <= startTime) {
		// If end is not after start, set it to start + 15 minutes
		const newEndTime = new Date(startTime.getTime() + 15 * 60000);
		return formatTimeForJSON(newEndTime);
	}

	return formatTimeForJSON(selectedTime);
};

export const formatDateForJSON = (date) => {
	if (date) {
		// Convert local date/time to UTC ISO string for server
		let iso = date.toISOString();
		return iso;
	}

	return null;
};

export const formateDateForDatePicker = (date) => {
	if (date) {
		// Convert UTC date from server to local date/time for display
		let formattedDate = new Date(date);
		return formattedDate;
	}

	return null;
};

export const dayMap = [ 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', ];
export const fullDayNames = [ 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', ];

// Common props for time-only DatePickers
export const timePickerProps = {
	showTimeSelect:     true,
	showTimeSelectOnly: true,
	timeFormat:         'h:mm aa',
	timeIntervals:      15,
	dateFormat:         'h:mm aa',
};

/**
 * Renders a weekday selector with checkboxes for each day of the week.
 * @param {Object} props
 * @param {number[]} props.selectedDays - Array of selected day indices (0=Sun, 6=Sat)
 * @param {function} props.onDayToggle - Callback when a day is toggled
 * @param {string} props.idSuffix - Suffix for input IDs to ensure uniqueness
 */
export const WeekdaySelector = ({
	selectedDays, onDayToggle, idSuffix = '',
}) => {
	return (
		<div className="weekday-selector">
			{dayMap.map((dayName, dayIndex) => {
				return (
					<React.Fragment key={dayIndex}>
						<input
							type="checkbox"
							id={`weekday-${dayName.toLowerCase()}${idSuffix}`}
							className="weekday"
							checked={(selectedDays || []).includes(dayIndex)}
							onChange={() => {
								onDayToggle(dayIndex);
							}}
							data-testing-info={buildTestData(`input--checkbox--${fullDayNames[dayIndex]}${idSuffix}`)}
						/>
						<label htmlFor={`weekday-${dayName.toLowerCase()}${idSuffix}`}>{dayName}</label>
					</React.Fragment>
				);
			})}
		</div>
	);
};

/**
 * Determines the initial scheduling mode from existing scheduling data.
 */
export const getInitialSchedulingMode = (scheduling) => {
	if (!scheduling) return '';
	if (scheduling.recurring_times_by_day && Object.keys(scheduling.recurring_times_by_day).length > 0) {
		return 'specific-days-with-times';
	} else if (scheduling.recurring_start_time || scheduling.recurring_end_time) {
		return 'daily-time-range';
	} else if (scheduling.days && scheduling.days.length > 0) {
		return 'all-day-specific-days';
	}
	return '';
};

/**
 * Cleans scheduling data based on active mode before saving.
 */
export const cleanSchedulingData = (scheduling, scheduled, schedulingMode) => {
	const cleanedScheduling = {
		...scheduling,
	};

	if (!scheduled) {
		cleanedScheduling.start_date = null;
		cleanedScheduling.end_date = null;
		cleanedScheduling.days = [];
		cleanedScheduling.recurring_start_time = null;
		cleanedScheduling.recurring_end_time = null;
		cleanedScheduling.recurring_times_by_day = {};
	} else if (schedulingMode === '') {
		// Keep the active date range when scheduling is enabled but no recurrence mode is selected.
		cleanedScheduling.days = [];
		cleanedScheduling.recurring_start_time = null;
		cleanedScheduling.recurring_end_time = null;
		cleanedScheduling.recurring_times_by_day = {};
	} else if (schedulingMode === 'all-day-specific-days') {
		cleanedScheduling.recurring_start_time = null;
		cleanedScheduling.recurring_end_time = null;
		cleanedScheduling.recurring_times_by_day = {};
	} else if (schedulingMode === 'daily-time-range') {
		cleanedScheduling.days = [];
		cleanedScheduling.recurring_times_by_day = {};
	} else if (schedulingMode === 'specific-days-with-times') {
		cleanedScheduling.recurring_start_time = null;
		cleanedScheduling.recurring_end_time = null;
	}

	return cleanedScheduling;
};

const placeholderTextStyle = { color: '#888' };

/**
 * "All Days Currently Active" is grayed-out placeholder text only while it is the
 * active selection (by default, or once picked) — never while another mode is active.
 */
export const getSchedulingPlaceholderStyle = (mode) => {
	return mode ? {} : placeholderTextStyle;
};

export const getSchedulingModeDisplayText = (mode) => {
	const modeLabels = {
		'all-day-specific-days':    'Show all day on specific days',
		'daily-time-range':         'Show every day within specific time range',
		'specific-days-with-times': 'Show on specific days within specific time ranges per day',
	};
	if (!mode) {
		return <span style={placeholderTextStyle}>All Days Currently Active</span>;
	}
	return modeLabels[mode] || mode;
};

export const SchedulingHelperText = () => (
	<div style={{ fontStyle: 'italic', fontSize: '12px', marginBottom: '8px', marginTop: '8px' }}>
		The item displays on All Active Dates, unless Recurrence settings are chosen below.
	</div>
);
